import { ErrorMessage } from "@hookform/error-message";
import i18next from "i18next";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { GrFormEdit } from "react-icons/gr";
import { MdClose } from "react-icons/md";
import { useCurrentUserLogin } from "../hooks/useCurrentUserLogin";
import { toast } from "react-toastify";
import {
  useDeleteControl,
  useGetCurrentUserTags,
  useUpdateControl,
} from "../api/hooks/useRiskThemes";
import { useAdmin } from "../context/adminContext";
import { useDesignSystem } from "../context/designSystemContext";
import { Categories } from "../types/categories";
import { ControlType } from "../types/controlsRisk";
import { canEditRisk as getCanEditRisk } from "../utils/canEditRisk";
import { showErrorToast } from "../utils/errorToast";

export const EditControlButton = ({
  control,
  riskTheme,
  refetchRiskTheme,
}: {
  control: ControlType;
  riskTheme: Categories;
  refetchRiskTheme: any;
}) => {
  const [editControl, setEditControl] = useState<boolean>(false);
  const isAdmin = Boolean(useAdmin());
  const { data: currentUserTags } = useGetCurrentUserTags();
  const currentUserLogin = useCurrentUserLogin();
  const canEditRisk = getCanEditRisk({
    isAdmin,
    riskTheme,
    currentUserLogin,
    currentUserTags,
  });
  
  const { designSystem } = useDesignSystem();

  const clickModal = () => {
    setEditControl(!editControl);
  };

  // Função para impedir a propagação do clique
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      id: control.id,
      name: control.name,
      plannedStartDate: control.plannedStartDate,
      actualStartDate: control.actualStartDate
        ? control.actualStartDate
        : undefined,
      plannedFinishDate: control.plannedFinishDate,
      actualFinishDate: control.actualFinishDate
        ? control.actualFinishDate
        : undefined,
      progressPerc: control.progressPerc,
      progress: control.progress,
      status: control.status,
      globalGoal: control.globalGoal,
    },
  });
  const initialDate = watch("plannedStartDate");
  const progressPerc = watch("progressPerc");

  const { mutate: updateControl } = useUpdateControl();

  const onSubmit = (data: any) => {
    //criar query de modificacao de controle update control
    const request: ControlType = {
      id: data.id,
      name: data.name,
      plannedStartDate: data.plannedStartDate,
      actualStartDate: data.actualStartDate,
      plannedFinishDate: data.plannedFinishDate,
      actualFinishDate: data.actualFinishDate,
      progressPerc: data.progressPerc,
      currentEvolution: data.currentEvolution
        ? {
            id: data.currentEvolution.id,
            description: data.currentEvolution.description,
            date: data.currentEvolution.date,
          }
        : undefined,
      status: data.status,
      globalGoal: data.globalGoal,
    };

    updateControl(request, {
      onSuccess: () => {
        refetchRiskTheme();
        reset({ ...request });
        setEditControl(false);
        toast.success("Controle alterado com sucesso");
      },
      onError: (error) => {
        showErrorToast(error, "Erro: Controle não salvo");
      },
    });
  };

  const { mutate: deleteControl } = useDeleteControl();

  const deleteButton = () => {
    if (
      window.confirm(
        `Você tem certeza que deseja apagar o controle?\nNome: ${control.name}`
      )
    ) {
      //TSK alterar i18n
      //query delete control by id
      deleteControl(control.id, {
        onSuccess: () => {
          refetchRiskTheme();
          setEditControl(false);
          toast.success("Controle deletado com sucesso");
        },
        onError: (error) => {
          showErrorToast(error, "Erro: controle nao deletado");
        },
      });
    }
  };

  const itemsCss = "flex items-center gap-2";
  const labelNamesCss =
    "font-openSans font-bold text-sextenary text-[13px] leading-[16px] tracking-[0.18px] min-w-[6rem]";

  useEffect(() => {
    reset(control);
  }, [control]);

  if (!canEditRisk) {
    return null;
  }

  return (
    <>
      <button
        className='h-[1.5rem] flex flex-row justify-center items-center'
        onClick={() => clickModal()}
      >
        <GrFormEdit className='w-5 h-5 align-middle' />
      </button>

      {editControl ? (
        <div
          className='fixed inset-0 bg-black bg-opacity-15 z-50 grid justify-items-end'
          onClick={() => {
            clickModal();
            reset();
          }}
        >
          <div
            className='fixed top-1/2 right-1/2 bg-white border rounded-xl h-[352px] min-h-fit w-[650px] -translate-y-1/2 translate-x-1/2 z-60 px-5 py-4 '
            onClick={stopPropagation}
          >
            {/* Ícone de fechar no canto superior direito */}
            <div className='absolute top-2 right-2'>
              <MdClose
                className='cursor-pointer text-red-500'
                size={24}
                onClick={() => setEditControl(false)}
              />
            </div>

            <div className='font-openSans font-bold text-sextenary text-[16px] leading-[16px] tracking-[0.18px] '>
              {i18next.t("control_editor")}
            </div>

            <form className='p-4' onSubmit={handleSubmit(onSubmit)}>
              <div className='my-4 flex flex-col gap-1.5'>
                <div className={itemsCss}>
                  <label className={labelNamesCss}>{i18next.t("name")}*:</label>
                  <input
                    className={`border border-black rounded px-1 w-1/2 min-h-[27.71px] ${
                      !canEditRisk ? "bg-gray-300" : ""
                    }`}
                    type='text'
                    readOnly={!canEditRisk}
                    {...register("name", {
                      required: i18next.t("required_field"),
                      minLength: {
                        value: 4,
                        message: i18next.t("min_4_characters_field"),
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='name'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                {/* inicio */}
                <div className={itemsCss}>
                  <label className={labelNamesCss}>
                    {i18next.t("control_initial_date")}*:
                  </label>
                  <input
                    className={`border border-black rounded px-1 min-w-[130px] ${
                      !canEditRisk ? "bg-gray-300" : ""
                    }`}
                    type='date'
                    readOnly={!canEditRisk}
                    {...register("plannedStartDate", {
                      required: i18next.t("required_field"),
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='plannedStartDate'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                {/* inicio real */}
                <div className={itemsCss}>
                  <label className={labelNamesCss}>
                    {i18next.t("control_actual_initial_date")}:
                  </label>
                  <input
                    className={`border border-black rounded px-1 min-w-[130px]`}
                    type='date'
                    {...register("actualStartDate", {
                      validate: (value: any) => {
                        const today = new Date(); // Data atual
                        const inputDate = new Date(value); // Data inserida

                        if (
                          progressPerc != 0 &&
                          progressPerc != 100 &&
                          !value
                        ) {
                          return i18next.t("need_to_start_progress");
                        }

                        if (progressPerc == 0) return true;
                        // Verificar se a data inserida é menor ou igual a hoje
                        return (
                          inputDate <= today ||
                          i18next.t("date_must_be_today_or_earlier")
                        );
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='actualStartDate'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={labelNamesCss}>
                    {i18next.t("control_term")}*:
                  </label>
                  <input
                    className={`border border-black rounded px-1 min-w-[130px] ${
                      !canEditRisk ? "bg-gray-300" : ""
                    }`}
                    type='date'
                    readOnly={!canEditRisk}
                    {...register("plannedFinishDate", {
                      required: i18next.t("required_field"),
                      validate: (value) => {
                        // date validation
                        if (!initialDate) return true;
                        return (
                          new Date(value) >= new Date(initialDate) ||
                          i18next.t("term_date_before_initial_date")
                        );
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='plannedFinishDate'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={labelNamesCss}>
                    {i18next.t("control_final_date")}:
                  </label>
                  <input
                    className='border border-black rounded px-1 min-w-[130px] min-h-[27.71px]'
                    type='date'
                    {...register("actualFinishDate", {
                      validate: (valueFinalDate) => {
                        // date validation
                        if (!valueFinalDate) return true;
                        if (
                          initialDate &&
                          new Date(valueFinalDate) < new Date(initialDate)
                        ) {
                          return i18next.t("final_date_before_initial_date");
                        }
                        return true;
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='actualFinishDate'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={labelNamesCss}>
                    {i18next.t("control_going")}*:
                  </label>
                  <input
                    className='border border-black rounded px-1 min-w-[130px] min-h-[27.71px]'
                    type='number'
                    min={0}
                    max={100}
                    {...register("progressPerc", {
                      required: i18next.t("required_field"),
                      validate: (value) => {
                        const finished = watch("actualFinishDate");
                        let actualStartDate = watch("actualStartDate");
                        if (finished && value != 100) {
                          return i18next.t(
                            "progress_must_be_100_when_finished"
                          );
                        }
                        if (actualStartDate && value == 0) {
                          return i18next.t(
                            "if_started_the_percentage_cannot_be_zero"
                          );
                        }
                        if (value == 100 && !(finished && actualStartDate)) {
                          return i18next.t("need_start_and_finished_date");
                        }
                        return true;
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='progressPerc'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={labelNamesCss}>{"OKR"}*:</label>
                  <select
                    defaultValue={control.globalGoal?.toString()}
                    className={`border border-black rounded px-1 min-w-[130px] min-h-[27.71px] ${
                      !canEditRisk ? "bg-gray-300" : ""
                    }`}
                    {...register("globalGoal", {
                      required: i18next.t("required_field"),
                    })}
                    disabled={!canEditRisk}
                  >
                    <option value='true'>Sim</option>
                    <option value='false'>Não</option>
                  </select>
                  <ErrorMessage
                    errors={errors}
                    name='globalGoal'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                {/* <div className={`flex flex-col gap-1`}>
                                    <label className={labelNamesCss}>Progresso:</label>
                                    <input className="border border-black rounded w-full px-1" type="text" {...register("progress")}></input>
                                    <ErrorMessage errors={errors} name="progress" render={({ message })=> <p>{message}</p>} />
                                </div> */}
              </div>

              <div className='flex justify-end gap-3'>
                <button
                  className='h-[32px] w-[81px] border rounded-md bg-red-500 text-white font-openSans font-bold text-sextenary text-[12px] leading-[13.98px]'
                  onClick={deleteButton}
                >
                  {i18next.t("button_delete")}
                </button>
                <button
                  className='h-[32px] w-[81px] border rounded-md text-white font-openSans font-bold text-sextenary text-[12px] leading-[13.98px]'
                  type='submit'
                  style={{
                    backgroundColor: designSystem?.cssBasics.primaryColor,
                  }}
                >
                  {i18next.t("button_save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};
